import { usePermissions } from '@/hooks/usePermissions'
import { useToasts } from '@/hooks/useToasts'
import * as Sentry from '@sentry/nextjs'

import { AccordionPanel, Flex, Grid, useDisclosure } from '@chakra-ui/react'

import {
  Button,
  ConfirmRemovalDialog,
  EditButton,
  LinkSecretProviderModal,
  PACS_SECRET_TEMPLATE,
  SettingsRow,
  formatSecretTemplate,
} from '@/components/ui'
import { useUnlinkCustomerSecretProviderMutation } from '@/graphql/generated/hooks'
import { CredentialLinkEntityType } from '@/graphql/generated/schemas'
import { useAppSelector } from '@/redux/hooks'
import { selectEditMode } from '@/redux/ui/uiSlice'
import { removeUnderscoreAndCapitalize } from '@/utils/formats'
import { SECURE_FIELD_MASK } from '@/utils/forms/optionConsts'

import {
  PacsSourceFieldInstanceI,
  PacsSourceInstanceQueryI,
} from '../types/types'

interface PACSSourceFieldsIProps {
  fields: PacsSourceFieldInstanceI[]
  index: number
  instanceId: string
  customerSecretProvider?: PacsSourceInstanceQueryI['customerSecretProvider']
  onEdit: () => void
  pacsSourceInstanceName: string
}

export const PACSSourceInstance = ({
  fields,
  index,
  instanceId,
  customerSecretProvider,
  onEdit,
  pacsSourceInstanceName,
}: PACSSourceFieldsIProps) => {
  const isEditMode = useAppSelector(selectEditMode)
  const { shouldShowCustomerSecretProviderSettings } = usePermissions()
  const { showSuccess, showError } = useToasts()
  const {
    isOpen: isUnlinking,
    onOpen: onStartUnlink,
    onClose: onUnlinkClose,
  } = useDisclosure()
  const {
    isOpen: isLinkOpen,
    onOpen: onLinkOpen,
    onClose: onLinkClose,
  } = useDisclosure()

  const [unlinkCustomerSecretProvider, { loading: isLoadingUnlink }] =
    useUnlinkCustomerSecretProviderMutation()

  const onUnlink = async () => {
    try {
      await unlinkCustomerSecretProvider({
        variables: {
          input: {
            entityId: instanceId,
            entityType: CredentialLinkEntityType.PacsSourceInstance,
          },
        },
        refetchQueries: ['PacsSourceSecretsSettings'],
      })
      showSuccess('Secret provider was disassociated')
    } catch (error) {
      showError('Failed to disassociate secret provider')
      Sentry.captureException(
        error ?? new Error('Failed to disassociate secret provider')
      )
    }
  }

  return (
    <>
      <AccordionPanel
        bgColor={index % 2 === 0 ? 'transparent' : 'white'}
        borderTop='1px solid #eaedf3'
        p='0'
      >
        <Flex alignItems='center' justifyContent='space-between' px='4' py='6'>
          <Grid
            columnGap='6'
            gridTemplateColumns='auto 1fr'
            rowGap='4'
            w='full'
          >
            <SettingsRow
              key={pacsSourceInstanceName}
              label='Name'
              value={pacsSourceInstanceName}
            />
            {fields?.map((field) => (
              <SettingsRow
                key={field.id}
                label={removeUnderscoreAndCapitalize(
                  field.pacsSourceField?.name
                )}
                value={
                  field.pacsSourceField?.secure
                    ? SECURE_FIELD_MASK
                    : field.value
                }
              />
            ))}
            {shouldShowCustomerSecretProviderSettings && (
              <>
                <SettingsRow
                  label='Secret Provider'
                  value={customerSecretProvider?.node?.providerName ?? 'None'}
                />
                {customerSecretProvider?.secretArn && (
                  <SettingsRow
                    label='Secret ARN'
                    value={customerSecretProvider.secretArn}
                  />
                )}
                {isEditMode &&
                  (customerSecretProvider?.node ? (
                    <Button onClick={onStartUnlink} size='sm' variant='link'>
                      Remove Secret Provider Association
                    </Button>
                  ) : (
                    <Button onClick={onLinkOpen} size='sm' variant='link'>
                      Add Secret Provider
                    </Button>
                  ))}
              </>
            )}
          </Grid>
          <Flex alignItems='center' flexShrink={0} gap={2} ml={4}>
            {isEditMode && <EditButton handleOnEdit={onEdit} />}
          </Flex>
        </Flex>
      </AccordionPanel>
      {isUnlinking && (
        <ConfirmRemovalDialog
          handleOnClick={onUnlink}
          hideCanBeUndone
          isLoading={isLoadingUnlink}
          isOpen={isUnlinking}
          itemTitle={customerSecretProvider?.node?.providerName ?? ''}
          onClose={onUnlinkClose}
          title='Remove Secret Provider Association'
        />
      )}
      {isLinkOpen && (
        <LinkSecretProviderModal
          entityId={instanceId}
          entityType={CredentialLinkEntityType.PacsSourceInstance}
          isOpen={isLinkOpen}
          onClose={onLinkClose}
          refetchQueryName='PacsSourceSecretsSettings'
          secretTemplate={formatSecretTemplate(PACS_SECRET_TEMPLATE)}
        />
      )}
    </>
  )
}
